<script setup>
// ============================================================================
// BonBoZ: MENU TRANG DA GOP — nam canh chuong thong bao tren thanh tren.
//
// Nut co BA dang, doi theo viec dang nhin gi:
//   - o Bang dieu khien -> avatar tai khoan + ten nguoi dung (chua vao trang
//     nao thi khong bay trang nao ra);
//   - dai dien ca tap gop -> chong avatar (toi da 3, du thi "+N") + ten nguoi
//     dung;
//   - dang kep trong mot trang -> dung avatar trang do + ten trang do.
// Ca ba dang deu xo ra cung mot danh sach khi ro chuot; bam mot trang la nhay
// thang sang cot hoi thoai cua trang do.
//
// Hai tang huy hieu do, cung mot nguon nen cong lai luon khop:
//   - goc tren phai NUT -> tong chua doc ca tap gop ("co tin moi khong");
//   - cuoi moi DONG trong danh sach -> chua doc cua rieng trang do ("o dau").
//
// VI SAO DOC `mergedInboxIds` CHU KHONG PHAI `selectedInboxIds`: xem ghi chu
// dai trong `composables/useSelectedPages.js`. Tom tat: `selectedInboxIds` bi
// ChatList ghi de con MOT trang moi lan dieu huong, nen neu menu doc no thi bam
// trang dau tien la danh sach tu rut xuong con mot muc.
// ============================================================================
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import Auth from 'dashboard/api/auth';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useSelectedPages } from 'dashboard/composables/useSelectedPages';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import UnreadBadge from 'next/Conversation/ConversationCard/UnreadBadge.vue';
import MergePagesDialog from 'dashboard/routes/dashboard/mainPage/MergePagesDialog.vue';
import QuanLyNhanVienDialog from 'dashboard/routes/dashboard/mainPage/QuanLyNhanVienDialog.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  pageIdentifier,
  platformIcon,
} from 'dashboard/routes/dashboard/mainPage/platforms';
import {
  DropdownBody,
  DropdownItem,
  DropdownSeparator,
} from 'dashboard/components-next/dropdown-menu/base';
import { provideDropdownContext } from 'dashboard/components-next/dropdown-menu/base/provider';

// So avatar ve ra trong nut. Qua 3 cai la chong nhau den muc khong con nhan ra
// trang nao, ma be ngang thi thanh tren khong con de cho.
const TOI_DA_HIEN = 3;

// Danh sach trang cao toi da bang 5 hang, du thi cuon.
//
// Tinh bang so thay vi viet thang `max-h-[17rem]` de doi mot trong hai so la
// chieu cao tu dung lai — mot hang = anh 32px + `p-2` tren duoi (8+8) = 48px,
// giua hai hang la `gap-2` = 8px.
const HANG_TRUOC_KHI_CUON = 5;
const CAO_MOT_HANG = 48;
const KHE_GIUA_HANG = 8;
const CAO_TOI_DA =
  HANG_TRUOC_KHI_CUON * CAO_MOT_HANG +
  (HANG_TRUOC_KHI_CUON - 1) * KHE_GIUA_HANG;

// Ro chuot ra khoi cum la dong — nhung cho mot nhip. Khong cho thi ngui tay
// tu nut xuong danh sach, di qua khe ho vai pixel la menu tat giua chung.
const NHIP_DONG = 220;

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const inboxes = useMapGetter('inboxes/getInboxes');
// Thanh trai (Sidebar.vue + actionCable) da nap va lam moi so nay, day chi doc
// lai. Can feature flag `conversation_unread_counts` bat tren tai khoan; tat
// thi getter tra 0 cho moi trang va huy hieu khong ve ra — khong hong gi.
const chuaDocTheoTrang = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);

const { mergedInboxIds, isAllMerged, selectedInboxIds, setSelectedInboxes } =
  useSelectedPages();
const { isAdmin } = useAdmin();

const dangMo = ref(false);
const hopThoaiGop = ref(null);
const hopThoaiNhanVien = ref(null);
let hen = null;

// Danh sach trang de liet ke. Mang rong = "tat ca trang" (quy uoc cua
// useSelectedPages), luc do lay nguyen danh sach hop thu.
//
// Khi co lua chon thi duyet theo THU TU ID DA LUU chu khong theo thu tu cua
// `inboxes`: nguoi dung nhin thay dung thu tu minh da tich. `.filter(Boolean)`
// vi mot trang da gop co the da bi xoa hop thu tu luc gop.
const trangDaGop = computed(() => {
  if (isAllMerged.value) return inboxes.value;
  const theoId = new Map(inboxes.value.map(i => [Number(i.id), i]));
  return mergedInboxIds.value.map(id => theoId.get(Number(id))).filter(Boolean);
});

// TRANG DANG XEM — mot trang duy nhat, hay dang nhin ca tap gop.
//
// `selectedInboxIds` chi co dung MOT id nghia la nguoi dung da chon han mot
// trang: ChatList ghi no moi lan dieu huong toi route co `inbox_id`, va
// `xemTatCa` o duoi tra no ve ca tap. Doc tu day chu khong tu `route.params`
// de con dung o ca nhung man hinh khong mang inbox tren duong dan (Bang dieu
// khien, Thong ke).
const trangDangXem = computed(() => {
  if (selectedInboxIds.value.length !== 1) return null;
  const id = Number(selectedInboxIds.value[0]);
  return trangDaGop.value.find(trang => Number(trang.id) === id) ?? null;
});

// O BANG DIEU KHIEN nut hien TAI KHOAN, khong hien trang nao ca.
//
// Day la man chon trang: chua buoc vao trang nao thi bay avatar trang ra la noi
// doi rang dang co ngu canh trang — dung cai bay ma `oTrangChinh` o TopBar.vue
// da mo ta cho dai dieu huong. Danh sach trang van xo ra binh thuong khi ro
// chuot, do moi la duong tat de nhay thang vao mot trang.
const oBangDieuKhien = computed(() => route.name === 'bonboz_main_page');

const tenNguoiDung = computed(() => currentUser.value?.available_name ?? '');

// Trang dang dai dien tren nut — `null` nghia la nut dang o dang "tai khoan"
// hoac dang "ca tap gop".
const trangTrenNut = computed(() =>
  oBangDieuKhien.value ? null : trangDangXem.value
);

const trangHienTrenNut = computed(() =>
  trangTrenNut.value
    ? [trangTrenNut.value]
    : trangDaGop.value.slice(0, TOI_DA_HIEN)
);

// Dang xem mot trang — hay dang o Bang dieu khien — thi khong con "con lai" nao
// de dem: chip `+N` chi co nghia khi nut dai dien cho CA tap gop.
const soConLai = computed(() => {
  if (oBangDieuKhien.value || trangTrenNut.value) return 0;
  return Math.max(0, trangDaGop.value.length - TOI_DA_HIEN);
});

// Nut hien ten TRANG khi dang xem mot trang, con lai thi hien ten NGUOI DUNG.
const nhanTrenNut = computed(
  () => trangTrenNut.value?.name ?? tenNguoiDung.value
);

// Chi bay loi ve khi dang bi kep trong mot trang VA tap gop con trang khac.
// Thieu no thi bam mot trang la ket: `selectedInboxIds` dung o trang do, khong
// con duong nao quay lai xem chung ca tap.
//
// O Bang dieu khien thi khong bay: o do nut dang o dang "tai khoan", khong he
// noi minh dang kep trong trang nao, nen mot dong "Tat ca N trang" hien ra la
// tra loi cho cau hoi nguoi dung chua dat.
const hienNutTatCa = computed(
  () => !!trangTrenNut.value && trangDaGop.value.length > 1
);

// So chua doc tren nut: LUON la tong cua CA tap gop, ke ca khi nut dang hien
// mot trang. Muon biet so cua tung trang thi mo menu ra — moi dong co huy hieu
// rieng.
//
// Vi sao khong dem rieng trang dang xem: huy hieu nam ngoai nut de tra loi cau
// "co tin moi o dau do trong cac trang cua minh khong" ma KHONG phai mo menu.
// Dem rieng trang dang xem thi tin cua bon trang kia im lang — dung cai viec ma
// huy hieu sinh ra de lam. Chuong thong bao canh ben cung dem kieu nay.
//
// Nguon la hoi thoai chua doc, cung getter voi thanh ben va badge "Hoi thoai",
// nen ba cho khong bao gio lech nhau. Luat dem o UnreadRule chi tinh hoi thoai
// `open` — hoi thoai chatbot dang giu (`pending`) khong duoc dem, o day cung
// nhu o moi cho khac trong app.
const soChuaDoc = computed(() =>
  trangDaGop.value.reduce(
    (tong, trang) => tong + chuaDocTheoTrang.value(trang.id),
    0
  )
);

const dong = () => {
  dangMo.value = false;
};

const huyHen = () => {
  if (hen) clearTimeout(hen);
  hen = null;
};

const roChuotVao = () => {
  huyHen();
  dangMo.value = true;
};

const roChuotRa = () => {
  huyHen();
  hen = setTimeout(dong, NHIP_DONG);
};

// DropdownItem doi mot <DropdownContainer> cha de lay `closeMenu`. O day tu
// cung cap ngu canh do: container cua Chatwoot chi mo bang CLICK, con cho nay
// phai mo duoc bang ro chuot.
provideDropdownContext({
  isOpen: dangMo,
  toggle: () => {
    dangMo.value = !dangMo.value;
  },
  closeMenu: dong,
});

// Bam mot trang = xem hoi thoai cua trang do. KHONG dung toi `mergedInboxIds`,
// nen sau khi nhay sang trang B thi menu van liet ke du ca tap da gop va nhay
// tiep sang C duoc ngay.
const chuyenTrang = inbox => {
  dong();
  router.push({
    name: 'inbox_dashboard',
    params: { accountId: accountId.value, inbox_id: inbox.id },
  });
};

// Quay ve xem chung ca tap gop. Phai di toi `home` (khong co `inbox_id`) chu
// khong dung lai cho: ChatList chi ghi de lua chon khi route MANG `inbox_id`,
// nen o duong dan khong co no thi tap gop vua dat lai moi giu duoc.
const xemTatCa = () => {
  dong();
  setSelectedInboxes(isAllMerged.value ? [] : mergedInboxIds.value);
  router.push({ name: 'home', params: { accountId: accountId.value } });
};

const moHopThoaiGop = () => {
  dong();
  hopThoaiGop.value?.open();
};

const moQuanLyNhanVien = () => {
  dong();
  hopThoaiNhanVien.value?.open();
};

const dangXuat = () => {
  dong();
  Auth.logout();
};

// Vao thang bang URL thi danh sach hop thu co the chua nap.
onMounted(() => {
  if (!inboxes.value.length) store.dispatch('inboxes/get');
});

onBeforeUnmount(huyHen);
</script>

<template>
  <div
    v-if="trangDaGop.length"
    v-on-click-outside="dong"
    class="relative"
    @mouseenter="roChuotVao"
    @mouseleave="roChuotRa"
  >
    <button
      type="button"
      class="flex relative gap-2 items-center h-9 rounded-lg transition-colors ps-1 pe-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[rgb(var(--bonboz-chu-khung))]"
      :class="
        dangMo
          ? 'bg-[rgb(var(--bonboz-chu-khung)/20%)]'
          : 'hover:bg-[rgb(var(--bonboz-chu-khung)/10%)]'
      "
      :aria-label="t('BONBOZ.MENU_TRANG.NHAN')"
      aria-haspopup="menu"
      :aria-expanded="dangMo"
      @click="dangMo = !dangMo"
      @keydown.escape="dong"
    >
      <!-- Chong avatar: `-ms-2` keo cai sau de len cai truoc, `first:ms-0` de
           cai dau khong bi thut vao trong.
           Vien dung `--pk-chrome` chu KHONG dung `--bonboz-nen-khung`: nen
           khung co the la gradient (chu de "hoang hon", xem helper/chuDe.js),
           ma `ring` chi nhan mot mau dac. `--pk-chrome` chinh la mau dai dien
           dac cua tung chu de, dat ra cho dung viec nay.
           `rounded-full` phai dat o DAY chu khong chi la prop cua Avatar: prop
           bo goc cho the ANH ben trong, con `ring` ve quanh the NGOAI — thieu
           dong nay thi vien ra hinh vuong. -->
      <span class="flex items-center shrink-0">
        <!-- O Bang dieu khien: avatar tai khoan, khong ve trang nao. -->
        <Avatar
          v-if="oBangDieuKhien"
          :src="currentUser.avatar_url"
          :name="tenNguoiDung"
          :size="26"
          rounded-full
          class="rounded-full ring-2 ring-[rgb(var(--pk-chrome))]"
        />
        <template v-else>
          <Avatar
            v-for="trang in trangHienTrenNut"
            :key="trang.id"
            :src="trang.avatar_url"
            :name="trang.name"
            :size="26"
            rounded-full
            class="-ms-2 first:ms-0 rounded-full ring-2 ring-[rgb(var(--pk-chrome))]"
          />
          <span
            v-if="soConLai"
            class="flex justify-center items-center -ms-2 text-xs font-medium rounded-full size-[26px] ring-2 ring-[rgb(var(--pk-chrome))] bg-[rgb(var(--bonboz-chu-khung)/25%)] text-[rgb(var(--bonboz-chu-khung))]"
          >
            {{ t('BONBOZ.MENU_TRANG.CON_LAI', { n: soConLai }) }}
          </span>
        </template>
      </span>
      <!-- Ten trang dang xem, hay ten nguoi dung khi dang dai dien ca tap gop.
           An duoi 1024px: tu 768 den 1023px thanh tren da chat (xem ghi chu be
           ngang o TopBar.vue), them mot chuoi ten nua la day chuong ra ngoai
           man. O DAY van `truncate` chu khong xuong dong nhu trong danh sach —
           thanh tren cao co dinh 56px, xuong dong la vo bo cuc. -->
      <span
        class="hidden max-w-36 text-sm truncate lg:inline text-[rgb(var(--bonboz-chu-khung))]"
      >
        {{ nhanTrenNut }}
      </span>

      <!-- Huy hieu chua doc, dat y het chuong thong bao va muc "Hoi thoai" tren
           thanh tren: de goc tren phai, vien mong mau khung de tach khoi nen.
           `ring` dung `--pk-chrome` vi `--bonboz-nen-khung` co the la gradient,
           ma `ring` chi nhan mot mau dac (xem chu thich o cum avatar tren). -->
      <UnreadBadge
        :count="soChuaDoc"
        class="absolute -top-1 -right-1 ring-1 ring-[rgb(var(--pk-chrome))]"
      />
    </button>

    <!-- `pt-2` chu khong phai `mt-2`: le trong nam TRONG the nen vung ro chuot
         lien mach tu nut xuong danh sach. Dung le ngoai thi giua hai cai co
         mot khe 8px khong thuoc ve ai, di chuot qua do la menu dong. -->
    <DropdownBody
      v-if="dangMo"
      class="khung-menu top-full z-50 pt-2 ltr:right-0 rtl:left-0 w-[min(20rem,calc(100vw-1rem))]"
    >
      <!-- Danh sach trang nam trong mot <li> rieng mang <ul> con: co vay moi
           cuon duoc RIENG phan trang, con Bang dieu khien / Che do gop trang /
           Tai khoan luon dinh o duoi, khong troi theo. <ul> long trong <li> la
           HTML hop le. -->
      <li class="min-w-0">
        <ul
          class="grid gap-2 pe-1 overflow-y-auto overscroll-contain list-none reset-base"
          :style="{ maxHeight: `${CAO_TOI_DA}px` }"
        >
          <li v-if="hienNutTatCa" class="min-w-0">
            <button
              type="button"
              class="flex gap-3 items-center p-2 w-full min-w-0 text-sm font-medium rounded-lg text-start text-n-slate-12 hover:bg-n-alpha-2"
              @click="xemTatCa"
            >
              <span
                class="i-lucide-layers size-8 shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
              {{ t('BONBOZ.MENU_TRANG.TAT_CA', { n: trangDaGop.length }) }}
            </button>
          </li>

          <!-- `min-w-0` tren <li>: <ul> cha la `grid`, ma grid item mac dinh
               `min-width: auto` — mot ten trang dai keo cot rong ra, vet sang
               khi ro chuot va ca chu deu tran khoi khung (thay ro 17/09/2026).
               `items-start` + `break-words`: ten dai thi XUONG DONG chu khong
               cat bang `truncate`; anh va icon kenh phai neo len dau dong, neo
               giua thi hang hai dong nhin lech. -->
          <li v-for="trang in trangDaGop" :key="trang.id" class="min-w-0">
            <button
              type="button"
              class="flex gap-3 items-start p-2 w-full min-w-0 rounded-lg text-start hover:bg-n-alpha-2"
              :class="
                trangDangXem && Number(trangDangXem.id) === Number(trang.id)
                  ? 'bg-n-alpha-2'
                  : ''
              "
              @click="chuyenTrang(trang)"
            >
              <Avatar
                :src="trang.avatar_url"
                :name="trang.name"
                :inbox="trang"
                :size="32"
                rounded-full
                class="shrink-0"
              />
              <span class="flex-1 min-w-0">
                <span
                  class="block text-sm font-medium break-words text-n-slate-12"
                >
                  {{ trang.name }}
                </span>
                <span class="block text-xs break-words text-n-slate-11">
                  {{ pageIdentifier(trang) }}
                </span>
              </span>
              <!-- Chua doc CUA RIENG TRANG NAY. Huy hieu tren nut chi cho biet
                   "co tin moi o dau do"; con day moi tra loi "o trang nao".
                   Cung getter nen cong cac dong lai bang dung so tren nut.
                   `mt-1` de neo len dong dau nhu icon kenh — ten trang dai thi
                   hang cao hai dong, neo giua se lech. -->
              <UnreadBadge
                :count="chuaDocTheoTrang(trang.id)"
                class="mt-1 shrink-0"
              />
              <span
                :class="platformIcon(trang)"
                class="mt-1 size-4 shrink-0"
                aria-hidden="true"
              />
            </button>
          </li>
        </ul>
      </li>

      <DropdownSeparator />

      <DropdownItem
        icon="i-lucide-layout-grid"
        :label="t('BONBOZ.TRANG_CHINH.TIEU_DE')"
        :link="{ name: 'bonboz_main_page', params: { accountId } }"
      />
      <DropdownItem
        icon="i-lucide-users-round"
        :label="t('BONBOZ.TRANG_CHINH.GOP_TITLE')"
        :click="moHopThoaiGop"
      />
      <!-- Chi admin: server cung chan, an o day cho gon menu cua nguoi truc. -->
      <DropdownItem
        v-if="isAdmin"
        icon="i-lucide-user-cog"
        :label="t('BONBOZ.QUAN_LY_NHAN_VIEN.TIEU_DE')"
        :click="moQuanLyNhanVien"
      />

      <DropdownSeparator />

      <!-- Hang tai khoan tu dung the thay vi <DropdownItem>: no phai chua HAI
           vung bam rieng (vao ho so / dang xuat), ma DropdownItem chi boc duoc
           mot the duy nhat — long mot <button> trong <router-link> la HTML sai
           va Firefox nuot mat cai ben trong. -->
      <li class="flex gap-1 items-center min-w-0">
        <RouterLink
          :to="{ name: 'profile_settings_index' }"
          class="flex flex-1 gap-3 items-center p-2 min-w-0 rounded-lg hover:bg-n-alpha-2"
          @click="dong"
        >
          <Avatar
            :src="currentUser.avatar_url"
            :name="tenNguoiDung"
            :status="currentUserAvailability"
            :size="32"
            rounded-full
            class="shrink-0"
          />
          <span class="min-w-0 text-start">
            <span class="block text-xs truncate text-n-slate-11">
              {{ t('BONBOZ.MENU_TRANG.TAI_KHOAN') }}
            </span>
            <span class="block text-sm font-medium truncate text-n-slate-12">
              {{ tenNguoiDung }}
            </span>
          </span>
        </RouterLink>
        <button
          type="button"
          class="flex justify-center items-center rounded-lg size-9 shrink-0 text-n-slate-11 hover:bg-n-alpha-2"
          :aria-label="t('SIDEBAR_ITEMS.LOGOUT')"
          :title="t('SIDEBAR_ITEMS.LOGOUT')"
          @click="dangXuat"
        >
          <span class="i-lucide-log-out size-4" aria-hidden="true" />
        </button>
      </li>
    </DropdownBody>

    <MergePagesDialog ref="hopThoaiGop" :inboxes="inboxes" />
    <QuanLyNhanVienDialog v-if="isAdmin" ref="hopThoaiNhanVien" />
  </div>
</template>

<style scoped>
/* `<ul>` cua DropdownBody la `grid`, ma grid item mac dinh `min-width: auto`:
   mot nhan dai keo cot rong ra va tran khoi khung. Cac <li> viet thang o file
   nay da co `min-w-0`, nhung <li> do <DropdownItem> tu ve thi khong cham vao
   duoc — component do dat `inheritAttrs: false` va do het $attrs vao the BEN
   TRONG, khong phai the <li>. Nen phai voi qua bang `:deep`. */
.khung-menu :deep(.n-dropdown-item) {
  min-width: 0;
}
</style>
